import { useEffect, useRef } from "react";
import { io } from "socket.io-client";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Module-singleton Socket.IO client. Every consumer that calls useSocketIo()
 * shares the same connection (auto-joined to user_<id> / role_<ROLE> /
 * region_<id> / chat_<conv_id> rooms server-side).
 *
 * Auth: pulls session_token from localStorage on connect.
 * Path: matches backend `socketio_path='/api/socket.io'`.
 *
 * Returns the live socket instance (or null if no token / disabled).
 */
let _socket = null;
let _refcount = 0;

function getSocket() {
  if (typeof window === "undefined") return null;
  const token = localStorage.getItem("session_token");
  if (!token || !BACKEND_URL) return null;
  if (_socket && _socket.connected) return _socket;
  if (_socket) return _socket; // still connecting

  _socket = io(BACKEND_URL, {
    path: "/api/socket.io",
    transports: ["websocket", "polling"],
    auth: { token },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });
  return _socket;
}

/**
 * Subscribe to one or more Socket.IO events.
 * @param {Object<string, Function>} handlers — map of event name → handler
 * @param {Object} options
 *   - enabled: defaults true. when false, the handlers aren't bound.
 */
export function useSocketIo(handlers, { enabled = true } = {}) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return undefined;
    const socket = getSocket();
    if (!socket) return undefined;
    _refcount += 1;

    const bound = {};
    Object.keys(handlersRef.current || {}).forEach((evt) => {
      const fn = (payload) => {
        try { handlersRef.current[evt]?.(payload); } catch { /* swallow */ }
      };
      bound[evt] = fn;
      socket.on(evt, fn);
    });

    return () => {
      Object.entries(bound).forEach(([evt, fn]) => socket.off(evt, fn));
      _refcount = Math.max(0, _refcount - 1);
      // Don't disconnect on every unmount — page navigations re-mount fast.
      // When all listeners are gone for a few minutes we could disconnect,
      // but keeping it open is cheap and avoids reconnect chatter.
    };
  }, [enabled]);
}

/** Imperative access to the singleton (e.g. emit team_join). */
export function getSocketIo() {
  return getSocket();
}
